import type { Instance } from "mobx-state-tree";
import { types, flow, getRoot } from "mobx-state-tree";
import * as repo from "./repository";
import type {
  FileTypeFork,
  FileTypeCategoryMapping,
  FilterGroup,
  FilterGroupItem,
} from "./repository";
import { DocumentCategory } from "./models";
import type { DocumentCategoryRow, DocumentCategorySnapshotIn } from "./models";
import type { AdminRootAccess } from "../rootAccess";
import {
  buildCategoryAssignmentOptions,
  categoryPathLabel,
} from "@/domains/admin/utils/categoryAssignmentOptions";
import type { TablesInsert, TablesUpdate } from "@/types/supabase-helpers";

export const DocumentCategoriesStore = types
  .model("DocumentCategoriesStore", {
    categories: types.array(DocumentCategory),
    // Every category at every level, for pickers that need the whole hierarchy.
    // Kept apart from `categories`, which other admin tables load as top-level only.
    categoryHierarchy: types.array(types.frozen<DocumentCategoryRow>()),
    isLoadingCategoryHierarchy: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    // File type category mappings (for bypassing/augmenting LLM classification)
    fileTypeMappings: types.array(types.frozen<FileTypeCategoryMapping>()),
    isLoadingMappings: types.optional(types.boolean, false),
    // Filter groups (decoupled filter hierarchy for UI)
    filterGroups: types.array(types.frozen<FilterGroup>()),
    isLoadingFilterGroups: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get categoriesList() {
      return self.categories.slice();
    },
    getById(id: number) {
      return self.categories.find((category) => category.id === id);
    },
    getByName(name: string) {
      return self.categories.find((category) => category.category_name === name);
    },
    /** Leaf categories, labelled with their parent path — the only valid mapping targets. */
    get categoryAssignmentOptions() {
      return buildCategoryAssignmentOptions(self.categoryHierarchy);
    },
    /** Full-path label for any category in the hierarchy, leaf or not. */
    categoryPathLabel(id: number) {
      return categoryPathLabel(id, new Map(self.categoryHierarchy.map((c) => [c.id, c])));
    },
    get fileTypeMappingsList() {
      return self.fileTypeMappings.slice();
    },
    getFileTypeMappingById(id: number) {
      return self.fileTypeMappings.find((mapping) => mapping.id === id);
    },
    get filterGroupsList() {
      return self.filterGroups.slice();
    },
    getFilterGroupById(id: number) {
      return self.filterGroups.find((group) => group.id === id);
    },
  }))
  .actions((self) => ({
    loadCategories: flow(function* loadCategories() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: DocumentCategoryRow[] = yield repo.listTopLevelDocumentCategories();
        self.categories.clear();
        data.forEach((category) => {
          self.categories.push(category as DocumentCategorySnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    loadAllCategories: flow(function* loadAllCategories() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: DocumentCategoryRow[] = yield repo.listDocumentCategories();
        self.categories.clear();
        data.forEach((category) => {
          self.categories.push(category as DocumentCategorySnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    loadCategoryHierarchy: flow(function* loadCategoryHierarchy() {
      self.isLoadingCategoryHierarchy = true;
      self.error = null;

      try {
        const data: DocumentCategoryRow[] = yield repo.listDocumentCategories();
        self.categoryHierarchy.replace(data);
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoadingCategoryHierarchy = false;
      }
    }),

    loadLeafCategories: flow(function* loadLeafCategories() {
      self.isLoading = true;
      self.error = null;

      try {
        const data: DocumentCategoryRow[] = yield repo.listLeafDocumentCategories();
        self.categories.clear();
        data.forEach((category) => {
          self.categories.push(category as DocumentCategorySnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    createCategory: flow(function* createCategory(category: TablesInsert<"document_categories">) {
      self.error = null;
      try {
        const newCategory: DocumentCategoryRow = yield repo.createDocumentCategory(category);
        self.categories.push(newCategory as DocumentCategorySnapshotIn);
        return newCategory;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateCategory: flow(function* updateCategory(
      id: number,
      updates: TablesUpdate<"document_categories">
    ) {
      self.error = null;
      try {
        const updatedCategory: DocumentCategoryRow = yield repo.updateDocumentCategory(id, updates);
        const index = self.categories.findIndex((cat) => cat.id === id);
        if (index !== -1) {
          self.categories.splice(index, 1, updatedCategory as DocumentCategorySnapshotIn);
        }
        return updatedCategory;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteCategory: flow(function* deleteCategory(id: number) {
      self.error = null;
      try {
        yield repo.deleteDocumentCategory(id);
        const index = self.categories.findIndex((cat) => cat.id === id);
        if (index !== -1) {
          self.categories.splice(index, 1);
        }
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    // ---------------------------------------------------------------------
    // Subcategory Refinement Configuration (hierarchy + rules)
    // ---------------------------------------------------------------------

    loadSubcategories: flow(function* loadSubcategories(parentCategoryId: number) {
      self.error = null;
      try {
        const rows: DocumentCategoryRow[] = yield repo.listDocumentSubcategories(parentCategoryId);
        return rows;
      } catch (e) {
        const message = `Failed to load subcategories for category ${parentCategoryId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    createSubcategory: flow(function* createSubcategory(
      category: TablesInsert<"document_categories">
    ) {
      self.error = null;
      try {
        const newCategory: DocumentCategoryRow = yield repo.createDocumentCategory(category);
        return newCategory;
      } catch (e) {
        const message = `Failed to create subcategory "${category.category_name}": ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    updateSubcategory: flow(function* updateSubcategory(
      id: number,
      updates: TablesUpdate<"document_categories">
    ) {
      self.error = null;
      try {
        const updatedCategory: DocumentCategoryRow = yield repo.updateDocumentCategory(id, updates);
        return updatedCategory;
      } catch (e) {
        const message = `Failed to update subcategory ${id}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    deleteSubcategory: flow(function* deleteSubcategory(id: number) {
      self.error = null;
      try {
        yield repo.deleteDocumentCategory(id);
      } catch (e) {
        const message = `Failed to delete subcategory ${id}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    loadCategorySubcategoryRuleFileTypes: flow(function* loadCategorySubcategoryRuleFileTypes(
      parentCategoryId: number
    ) {
      self.error = null;
      try {
        const fileTypes: string[] =
          yield repo.getCategorySubcategoryRuleFileTypes(parentCategoryId);
        return fileTypes;
      } catch (e) {
        const message = `Failed to load file type rules for category ${parentCategoryId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    setCategorySubcategoryRuleFileTypes: flow(function* setCategorySubcategoryRuleFileTypes(
      parentCategoryId: number,
      fileTypes: string[]
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to update category refinement rules");
        }
        yield repo.setCategorySubcategoryRuleFileTypes(parentCategoryId, fileTypes, userProfileId);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to set file type rules for category ${parentCategoryId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    // ---------------------------------------------------------------------
    // Shared Subcategories (borrowed from other parent categories)
    // ---------------------------------------------------------------------

    /**
     * Load shared subcategory IDs for a parent category.
     * Returns IDs of subcategories that are "borrowed" from other parents.
     */
    loadSharedSubcategories: flow(function* loadSharedSubcategories(parentCategoryId: number) {
      self.error = null;
      try {
        const ids: number[] = yield repo.getSharedSubcategories(parentCategoryId);
        return ids;
      } catch (e) {
        const message = `Failed to load shared subcategories for category ${parentCategoryId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Replace all shared subcategories for a parent category.
     * Removes existing shared relationships and inserts new ones.
     */
    setSharedSubcategories: flow(function* setSharedSubcategories(
      parentCategoryId: number,
      subcategoryIds: number[]
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to update shared subcategories");
        }
        yield repo.setSharedSubcategories(parentCategoryId, subcategoryIds, userProfileId);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to set shared subcategories for category ${parentCategoryId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Load all subcategories in the system (for selection in shared subcategories UI).
     * Returns categories that have a parent_category_id (not top-level).
     */
    loadAllSubcategories: flow(function* loadAllSubcategories() {
      self.error = null;
      try {
        const rows: DocumentCategoryRow[] = yield repo.listAllSubcategories();
        return rows;
      } catch (e) {
        const message = `Failed to load all subcategories: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    // ---------------------------------------------------------------------
    // File Type Forks (subcategory routing by file type)
    // ---------------------------------------------------------------------

    /**
     * Load all file type forks for a parent category.
     * Each fork defines which subcategories are used for specific file types.
     */
    loadFileTypeForks: flow(function* loadFileTypeForks(parentCategoryId: number) {
      self.error = null;
      try {
        const forks: FileTypeFork[] = yield repo.getFileTypeForks(parentCategoryId);

        // Load subcategory IDs for each fork
        for (const fork of forks) {
          const subcategoryIds: number[] = yield repo.getForkSubcategories(fork.id);
          (fork as FileTypeFork & { subcategory_ids: number[] }).subcategory_ids = subcategoryIds;
        }

        return forks as (FileTypeFork & { subcategory_ids: number[] })[];
      } catch (e) {
        const message = `Failed to load file type forks for category ${parentCategoryId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Create a new file type fork.
     */
    createFileTypeFork: flow(function* createFileTypeFork(
      parentCategoryId: number,
      fileTypes: string[],
      forkName: string | null,
      priority: number
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to create file type forks");
        }
        const fork: FileTypeFork = yield repo.createFileTypeFork(
          parentCategoryId,
          fileTypes,
          forkName,
          priority,
          userProfileId
        );
        root.invalidateM2MCache();
        return fork;
      } catch (e) {
        const message = `Failed to create file type fork: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Update a file type fork.
     */
    updateFileTypeFork: flow(function* updateFileTypeFork(
      forkId: number,
      updates: Partial<Pick<FileTypeFork, "file_types" | "fork_name" | "priority">>
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to update file type forks");
        }
        const fork: FileTypeFork = yield repo.updateFileTypeFork(forkId, updates, userProfileId);
        root.invalidateM2MCache();
        return fork;
      } catch (e) {
        const message = `Failed to update file type fork ${forkId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Delete a file type fork.
     */
    deleteFileTypeFork: flow(function* deleteFileTypeFork(forkId: number) {
      self.error = null;
      try {
        yield repo.deleteFileTypeFork(forkId);
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to delete file type fork ${forkId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Set subcategories for a file type fork (replaces existing links).
     */
    setForkSubcategories: flow(function* setForkSubcategories(
      forkId: number,
      subcategoryIds: number[]
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to set fork subcategories");
        }
        yield repo.setForkSubcategories(forkId, subcategoryIds, userProfileId);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to set subcategories for fork ${forkId}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    // ---------------------------------------------------------------------
    // File Type Category Mappings (bypass/augment LLM classification)
    // ---------------------------------------------------------------------

    /**
     * Load all file type category mappings.
     */
    loadFileTypeMappings: flow(function* loadFileTypeMappings() {
      self.isLoadingMappings = true;
      self.error = null;
      try {
        const data: FileTypeCategoryMapping[] = yield repo.listFileTypeCategoryMappings();
        self.fileTypeMappings.clear();
        data.forEach((mapping) => {
          self.fileTypeMappings.push(mapping);
        });
      } catch (e) {
        const message = `Failed to load file type mappings: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      } finally {
        self.isLoadingMappings = false;
      }
    }),

    /**
     * Create a new file type category mapping.
     */
    createFileTypeMapping: flow(function* createFileTypeMapping(mapping: {
      file_types: string[];
      category_id: number;
      assignment_mode: "skip_llm" | "additional";
      parent_only?: boolean;
      priority?: number;
      mapping_name?: string | null;
      description?: string | null;
    }) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to create file type mappings");
        }
        const newMapping: FileTypeCategoryMapping = yield repo.createFileTypeCategoryMapping(
          mapping,
          userProfileId
        );
        self.fileTypeMappings.push(newMapping);
        root.invalidateM2MCache();
        return newMapping;
      } catch (e) {
        const message = `Failed to create file type mapping: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Update an existing file type category mapping.
     */
    updateFileTypeMapping: flow(function* updateFileTypeMapping(
      id: number,
      updates: Partial<{
        file_types: string[];
        category_id: number;
        assignment_mode: "skip_llm" | "additional";
        parent_only: boolean;
        priority: number;
        mapping_name: string | null;
        description: string | null;
      }>
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to update file type mappings");
        }
        const updatedMapping: FileTypeCategoryMapping = yield repo.updateFileTypeCategoryMapping(
          id,
          updates,
          userProfileId
        );
        const index = self.fileTypeMappings.findIndex((m) => m.id === id);
        if (index !== -1) {
          self.fileTypeMappings.splice(index, 1, updatedMapping);
        }
        root.invalidateM2MCache();
        return updatedMapping;
      } catch (e) {
        const message = `Failed to update file type mapping ${id}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Delete a file type category mapping.
     */
    deleteFileTypeMapping: flow(function* deleteFileTypeMapping(id: number) {
      self.error = null;
      try {
        yield repo.deleteFileTypeCategoryMapping(id);
        const index = self.fileTypeMappings.findIndex((m) => m.id === id);
        if (index !== -1) {
          self.fileTypeMappings.splice(index, 1);
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to delete file type mapping ${id}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Create a category that is only ever assigned deterministically — by a file-type
     * mapping or by a template link — and never chosen by the LLM. These categories have
     * category_type = 'file-type', which is what keeps them out of classification prompts.
     */
    createAutoAssignedCategory: flow(function* createAutoAssignedCategory(
      categoryName: string,
      description: string | null
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to create categories");
        }
        const newCategory: DocumentCategoryRow = yield repo.createAutoAssignedCategory(
          categoryName,
          description,
          userProfileId
        );
        // Add to local categories list for immediate UI update
        self.categories.push(newCategory as DocumentCategorySnapshotIn);
        self.categoryHierarchy.push(newCategory);
        return newCategory;
      } catch (e) {
        const message = `Failed to create category: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    // ---------------------------------------------------------------------
    // Filter Groups (decoupled filter hierarchy for UI)
    // ---------------------------------------------------------------------

    /**
     * Load all filter groups.
     */
    loadFilterGroups: flow(function* loadFilterGroups() {
      self.isLoadingFilterGroups = true;
      self.error = null;
      try {
        const data: FilterGroup[] = yield repo.listFilterGroups();
        self.filterGroups.clear();
        data.forEach((group) => {
          self.filterGroups.push(group);
        });
      } catch (e) {
        const message = `Failed to load filter groups: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      } finally {
        self.isLoadingFilterGroups = false;
      }
    }),

    /**
     * Create a new filter group.
     */
    createFilterGroup: flow(function* createFilterGroup(
      label: string,
      labelEt: string | null,
      linkedCategoryId: number | null
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to create filter groups");
        }
        const newGroup: FilterGroup = yield repo.createFilterGroup(
          label,
          labelEt,
          linkedCategoryId,
          userProfileId
        );
        self.filterGroups.push(newGroup);
        root.invalidateM2MCache();
        return newGroup;
      } catch (e) {
        const message = `Failed to create filter group: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Update an existing filter group.
     */
    updateFilterGroup: flow(function* updateFilterGroup(
      id: number,
      updates: { label?: string; label_et?: string | null; linked_category_id?: number | null }
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to update filter groups");
        }
        const updatedGroup: FilterGroup = yield repo.updateFilterGroup(id, updates, userProfileId);
        const index = self.filterGroups.findIndex((g) => g.id === id);
        if (index !== -1) {
          self.filterGroups.splice(index, 1, updatedGroup);
        }
        root.invalidateM2MCache();
        return updatedGroup;
      } catch (e) {
        const message = `Failed to update filter group ${id}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Delete a filter group.
     */
    deleteFilterGroup: flow(function* deleteFilterGroup(id: number) {
      self.error = null;
      try {
        yield repo.deleteFilterGroup(id);
        const index = self.filterGroups.findIndex((g) => g.id === id);
        if (index !== -1) {
          self.filterGroups.splice(index, 1);
        }
        const root = getRoot<AdminRootAccess>(self);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to delete filter group ${id}: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Load items for a specific filter group.
     */
    loadFilterGroupItems: flow(function* loadFilterGroupItems(filterGroupId: number) {
      self.error = null;
      try {
        const items: FilterGroupItem[] = yield repo.listFilterGroupItems(filterGroupId);
        return items;
      } catch (e) {
        const message = `Failed to load filter group items: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Load all filter group items across all groups.
     * Useful for determining which categories are already assigned.
     */
    loadAllFilterGroupItems: flow(function* loadAllFilterGroupItems() {
      self.error = null;
      try {
        const items: FilterGroupItem[] = yield repo.listAllFilterGroupItems();
        return items;
      } catch (e) {
        const message = `Failed to load all filter group items: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    /**
     * Set items for a filter group (replaces existing items).
     */
    setFilterGroupItems: flow(function* setFilterGroupItems(
      filterGroupId: number,
      categoryIds: number[]
    ) {
      self.error = null;
      try {
        const root = getRoot<AdminRootAccess>(self);
        const userProfileId = root.authStore.userProfileId;
        if (!userProfileId) {
          throw new Error("User must be authenticated to set filter group items");
        }
        yield repo.setFilterGroupItems(filterGroupId, categoryIds, userProfileId);
        root.invalidateM2MCache();
      } catch (e) {
        const message = `Failed to set filter group items: ${e instanceof Error ? e.message : String(e)}`;
        self.error = message;
        throw new Error(message);
      }
    }),

    clear() {
      self.categories.clear();
      self.fileTypeMappings.clear();
      self.filterGroups.clear();
      self.isLoading = false;
      self.isLoadingMappings = false;
      self.isLoadingFilterGroups = false;
      self.error = null;
    },
  }));

export type DocumentCategoriesStoreInstance = Instance<typeof DocumentCategoriesStore>;
